const BACKEND_URI = "";
const USE_GPT4V = false;
const AZURE_SEARCH_SEMANTIC_RANKER = "free";
const USE_VECTORS = true;
const USE_USER_UPLOAD = false;
const SHOW_CHAT_HISTORY_BROWSER = false;
const SHOW_CHAT_HISTORY_POSTGRES = true;

import {
    ChatAppResponse,
    ChatAppResponseOrError,
    ChatAppRequest,
    Config,
    SimpleAPIResponse,
    HistoryListApiResponse,
    HistoryApiResponse,
    BotAppResponse
} from "./models";
import { useLogin, appServicesToken } from "../authConfig";

export function getHeaders(idToken: string | undefined): Record<string, string> {
    // If using login and not using app services, add the id token of the logged in account as the authorization
    if (useLogin && appServicesToken == null) {
        if (idToken) {
            return { Authorization: `Bearer ${idToken}` };
        }
    }

    return {};
}

export const configApi = (): Config => {
    return {
        showGPT4VOptions: USE_GPT4V,
        showSemanticRankerOption: AZURE_SEARCH_SEMANTIC_RANKER.toLowerCase() != "disabled",
        showVectorOption: USE_VECTORS,
        showUserUpload: USE_USER_UPLOAD,
        showChatHistoryBrowser: SHOW_CHAT_HISTORY_BROWSER,
        showChatHistoryPostgres: SHOW_CHAT_HISTORY_POSTGRES
    };
};

export async function askApi(request: ChatAppRequest, idToken: string | undefined): Promise<ChatAppResponse> {
    const response = await fetch(`${BACKEND_URI}/ask`, {
        method: "POST",
        headers: { ...getHeaders(idToken), "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });

    const parsedResponse: ChatAppResponseOrError = await response.json();
    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || "Unknown error");
    }

    return parsedResponse as ChatAppResponse;
}

export async function chatApi(request: ChatAppRequest, idToken: string | undefined): Promise<Response> {
    return await fetch(`${BACKEND_URI}/chat`, {
        method: "POST",
        headers: { ...getHeaders(idToken), "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });
}

export async function chatStreamApi(request: ChatAppRequest, idToken: string | undefined, signal?: AbortSignal): Promise<Response> {
    return await fetch(`${BACKEND_URI}/chat/stream`, {
        method: "POST",
        headers: { ...getHeaders(idToken), "Content-Type": "application/json" },
        body: JSON.stringify(request),
        signal
    });
}

export async function chatCancelApi(chat_id: string, idToken: string | undefined): Promise<Response> {
    return await fetch(`${BACKEND_URI}/chat/cancel`, {
        method: "POST",
        headers: { ...getHeaders(idToken), "Content-Type": "application/json" },
        body: JSON.stringify({ chat_id })
    });
}

export function getCitationFilePath(citation: string): string {
    return `${BACKEND_URI}/content/${citation}`;
}

export async function uploadFileApi(request: FormData, idToken: string): Promise<SimpleAPIResponse> {
    const response = await fetch("/upload", {
        method: "POST",
        headers: getHeaders(idToken),
        body: request
    });

    if (!response.ok) {
        throw new Error(`Uploading files failed: ${response.statusText}`);
    }

    const dataResponse: SimpleAPIResponse = await response.json();
    return dataResponse;
}

export async function deleteUploadedFileApi(filename: string, idToken: string): Promise<SimpleAPIResponse> {
    const response = await fetch("/delete_uploaded", {
        method: "POST",
        headers: { ...getHeaders(idToken), "Content-Type": "application/json" },
        body: JSON.stringify({ filename })
    });

    if (!response.ok) {
        throw new Error(`Deleting file failed: ${response.statusText}`);
    }

    const dataResponse: SimpleAPIResponse = await response.json();
    return dataResponse;
}

export async function listUploadedFilesApi(idToken: string): Promise<string[]> {
    const response = await fetch(`/list_uploaded`, {
        method: "GET",
        headers: getHeaders(idToken)
    });

    if (!response.ok) {
        throw new Error(`Listing files failed: ${response.statusText}`);
    }

    const dataResponse: string[] = await response.json();
    return dataResponse;
}

export async function postChatHistoryApi(item: any, idToken: string): Promise<any> {
    // In the new Postgres/LangGraph model, history is saved automatically by the backend.
    // We might use this to update the title if needed, or just return success.
    // For now, let's treat it as a no-op or update title if item contains it.
    // The backend `create_chat` or `touch_chat` handles persistence.
    return {};
}
export async function getChatHistoryListApi(
    count: number,
    continuationToken: string | undefined,
    localId: String,
    idToken: string
): Promise<HistoryListApiResponse> {
    const headers = await getHeaders(idToken);
    const offset = continuationToken ? parseInt(continuationToken) : 0;
    let url = `${BACKEND_URI}/chats?limit=${count}&offset=${offset}`;

    const response = await fetch(url.toString(), {
        method: "GET",
        headers: { ...headers, "Content-Type": "application/json" }
    });
    if (!response.ok) {
        throw new Error(`Getting chat histories failed: ${response.statusText}`);
    }
    const chats = await response.json();
    // Map backend Chat model to frontend expectation (timestamp)
    const mappedChats = chats.map((c: any) => ({
        ...c,
        timestamp: c.updated_at
    }));
    return {
        sessions: mappedChats,
        continuation_token: chats.length < count ? undefined : (offset + chats.length).toString()
    };
}
export async function getChatHistoryApi(id: string, idToken: string, localId: string): Promise<HistoryApiResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/chats/${id}/messages`, {
        method: "GET",
        headers: { ...headers, "Content-Type": "application/json" }
    });
    if (!response.ok) {
        throw new Error(`Getting chat history failed: ${response.statusText}`);
    }
    const data = await response.json();

    // Transform linear messages to pairs for frontend compatibility
    const answers: [string, BotAppResponse][] = [];
    let lastUserMsg: string | null = null;
    if (data.messages) {
        for (const msg of data.messages) {
            const content = msg.message || msg.content;
            if (msg.role === "user") {
                lastUserMsg = content;
            } else if (msg.role === "assistant" && lastUserMsg) {
                answers.push([
                    lastUserMsg,
                    {
                        message: content,
                        session_state: id,
                        followup_questions: null,
                        isHistory: true
                    }
                ]);
                lastUserMsg = null;
            }
        }
    }

    return {
        answers: answers,
        id: id,
        entra_oid: localId as string
    };
}
export async function renameChatHistoryApi(id: string, title: string, idToken: string, localId: string): Promise<any> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/chats/${id}`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ title: title })
    });
    if (!response.ok) {
        throw new Error(`Renaming chat history failed: ${response.statusText}`);
    }
}
export async function deleteChatHistoryApi(id: string, idToken: string, localId: string): Promise<any> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/chats/${id}`, {
        method: "DELETE",
        headers: { ...headers, "Content-Type": "application/json" }
    });
    if (!response.ok) {
        throw new Error(`Deleting chat history failed: ${response.statusText}`);
    }
}
